import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Typography,
  TextField,
  Button,
  CircularProgress,
  Grid
} from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import api from "../../services/api";
import { toast } from "react-toastify";
import { getStandardButtonStyles } from "../../styles/standardButtons";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const standardButtons = getStandardButtonStyles(theme);

  return {
    ...standardButtons,
    pageContainer: {
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: isDark ? "#131518" : "#F8FAFC",
      padding: "40px 16px",
      boxSizing: "border-box",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      transition: "background-color 0.2s ease"
    },
    cardPaper: {
      width: "100%",
      maxWidth: 520,
      backgroundColor: isDark ? "#1E2126" : "#FFFFFF",
      borderRadius: "16px !important",
      border: `1px solid ${theme.palette.primary.main} !important`,
      boxShadow: isDark
        ? `0 0 16px ${theme.palette.primary.main}80 !important`
        : `0 0 12px ${theme.palette.primary.main}40 !important`,
      padding: "36px 32px",
      boxSizing: "border-box",
      transition: "all 0.2s ease"
    },
    title: {
      color: isDark ? "#FFFFFF" : "#0F172A",
      fontWeight: 700,
      fontSize: "1.45rem",
      letterSpacing: "-0.02em",
      textAlign: "center"
    },
    description: {
      color: isDark ? "#94A3B8" : "#64748B",
      marginTop: 8,
      lineHeight: 1.55,
      fontSize: "0.9rem",
      textAlign: "center"
    },
    requiredBadge: {
      fontSize: "0.8rem",
      color: isDark ? "#ff6b6b" : "#dc2626",
      fontWeight: 600,
      textAlign: "right",
      marginBottom: 12
    },
    inputField: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 8,
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.02)",
        "& fieldset": {
          borderColor: isDark ? "rgba(255, 255, 255, 0.18)" : "#CBD5E1",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        },
        "&:hover fieldset": {
          borderColor: theme.palette.primary.main,
        },
        "&.Mui-focused fieldset": {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 6px ${theme.palette.primary.main}50`,
        },
      },
      "& .MuiInputBase-input": {
        fontSize: "0.88rem",
        color: isDark ? "#FFFFFF" : "#0F172A",
      },
      "& .MuiInputLabel-outlined": {
        color: isDark ? "#FFFFFF !important" : "#334155 !important",
        fontSize: "0.88rem",
        fontWeight: 500,
        "&.MuiInputLabel-shrink": {
          color: isDark ? "#FFFFFF !important" : "#334155 !important",
          fontWeight: 500,
        },
        "&.Mui-focused": {
          color: isDark ? "#FFFFFF !important" : `${theme.palette.primary.main} !important`,
          fontWeight: 600,
        },
        "&.Mui-error": {
          color: isDark ? "#ff6b6b !important" : "#dc2626 !important",
        },
      },
      "& .MuiFormHelperText-root": {
        fontSize: "0.78rem",
        marginTop: 4,
        "&.Mui-error": {
          color: isDark ? "#ff6b6b !important" : "#dc2626 !important",
        },
      },
    },
    submitButton: {
      ...standardButtons.applyButton,
      width: "100%",
      height: 42,
      justifyContent: "center",
      fontSize: "0.92rem",
      marginTop: 8,
      boxShadow: `0 0 10px ${theme.palette.primary.main}33`,
    },
    successCard: {
      width: "100%",
      maxWidth: 500,
      backgroundColor: isDark ? "#1E2126" : "#FFFFFF",
      borderRadius: "16px !important",
      border: `1px solid ${theme.palette.primary.main} !important`,
      boxShadow: isDark
        ? `0 0 16px ${theme.palette.primary.main}80 !important`
        : `0 0 12px ${theme.palette.primary.main}40 !important`,
      padding: "44px 32px",
      textAlign: "center",
      boxSizing: "border-box"
    },
    successIconWrapper: {
      width: 60,
      height: 60,
      borderRadius: "50%",
      backgroundColor: isDark ? "rgba(16, 185, 129, 0.15)" : "#ecfdf5",
      color: "#10b981",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 18px auto"
    },
    successTitle: {
      color: isDark ? "#FFFFFF" : "#0F172A",
      fontWeight: 700,
      marginBottom: 8
    },
    successText: {
      color: isDark ? "#94A3B8" : "#64748B",
      lineHeight: 1.6,
      marginBottom: 20
    },
    successFooterBadge: {
      padding: "10px 16px",
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f1f5f9",
      borderRadius: 8,
      fontSize: "0.85rem",
      color: isDark ? "#94A3B8" : "#475569",
      fontWeight: 500,
      display: "inline-block"
    }
  };
});

const PublicForm = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";

  const { slug } = useParams();
  const query = useQuery();
  const ticketId = query.get("ticketId");

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Mantiene los valores ingresados por el usuario
  const [formData, setFormData] = useState({});
  // Errores de validación
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const { data } = await api.get(`/public/forms/${slug}`);
        const fields = typeof data.fields === "string" ? JSON.parse(data.fields) : data.fields;
        setForm({ ...data, fields });
      } catch (err) {
        toast.error("Formulario no encontrado o no disponible.");
      }
      setLoading(false);
    };
    fetchForm();
  }, [slug]);

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (form && form.fields) {
      form.fields.forEach(field => {
        if (field.required) {
          const val = formData[field.name];
          if (val === undefined || val === null || String(val).trim() === "") {
            newErrors[field.name] = "Este campo es obligatorio";
          }
        }
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.warn("Por favor completa los campos obligatorios marcados con asterisco (*).");
      return;
    }

    if (!ticketId) {
      toast.error("Enlace incompleto. No se detectó el identificador de conversación (ticketId).");
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/public/forms/${slug}/submit`, {
        ticketId,
        data: formData
      });
      setSuccess(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Ocurrió un error al enviar tus respuestas.");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className={classes.pageContainer}>
        <CircularProgress style={{ color: theme.palette.primary.main }} />
      </div>
    );
  }

  if (!form) {
    return (
      <div className={classes.pageContainer}>
        <div className={classes.cardPaper}>
          <Typography variant="h6" className={classes.title}>
            Formulario no encontrado
          </Typography>
          <Typography variant="body2" className={classes.description}>
            El formulario solicitado no existe o el enlace ya no está disponible.
          </Typography>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={classes.pageContainer}>
        <div className={classes.successCard}>
          <div className={classes.successIconWrapper}>
            <CheckCircleOutlineIcon style={{ fontSize: 34 }} />
          </div>
          <Typography variant="h5" className={classes.successTitle}>
            ¡Datos Enviados con Éxito!
          </Typography>
          <Typography variant="body2" className={classes.successText}>
            Hemos recibido tus respuestas correctamente. Tu conversación en WhatsApp continuará de forma automática.
          </Typography>
          <div className={classes.successFooterBadge}>
            Ya puedes cerrar esta ventana y regresar a tu chat.
          </div>
        </div>
      </div>
    );
  }

  const hasRequiredFields = form.fields && form.fields.some(f => f.required);

  return (
    <div className={classes.pageContainer}>
      <div className={classes.cardPaper}>
        {/* Cabecera del Formulario */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <Typography variant="h5" className={classes.title}>
            {form.title || form.name}
          </Typography>
          {form.description && (
            <Typography variant="body2" className={classes.description}>
              {form.description}
            </Typography>
          )}
        </div>

        {/* Indicador de campos obligatorios con O mayúscula */}
        {hasRequiredFields && (
          <div className={classes.requiredBadge}>
            * Campos Obligatorios
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            {form.fields && form.fields.map((field, idx) => {
              const hasError = Boolean(errors[field.name]);
              const isTextarea = field.type === "textarea";

              return (
                <Grid item xs={12} key={idx}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    className={classes.inputField}
                    label={
                      <span>
                        {field.label}
                        {field.required && (
                          <span style={{ color: isDark ? "#ff6b6b" : "#dc2626", marginLeft: 4 }}>*</span>
                        )}
                      </span>
                    }
                    type={isTextarea ? "text" : field.type}
                    multiline={isTextarea}
                    rows={isTextarea ? 3 : 1}
                    value={formData[field.name] || ""}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    error={hasError}
                    helperText={hasError ? errors[field.name] : ""}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              );
            })}

            <Grid item xs={12} style={{ marginTop: 8 }}>
              <Button
                type="submit"
                disabled={submitting}
                className={classes.submitButton}
              >
                {submitting ? (
                  <CircularProgress size={20} style={{ color: isDark ? "#FFFFFF" : theme.palette.primary.main }} />
                ) : (
                  "Enviar y Continuar"
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
};

export default PublicForm;
